<template>
    <div :class="[ 'add-collateral-widget' ]">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ steps[step].title || 'Add New Collateral' }}</h3>
                <div class="widget-header-actions">
                    <div class="svg-container widget-close-button" @click="closeWidget">
                        <widget-close-svg />
                    </div>
                </div>
            </div>
            <div class="content-wrapper">
                <div v-show="step == 'AddCollateral'">
                    <div class="add-item-container">
                        <div class="add-item">
                            <div class="add-item-inner-container">
                                <div class="bg-wrapper">
                                   <div ref="dragDropArea" 
                                    :class="{ 'dropping-files': draggedFileEnteredArea }" 
                                    class="add-item-upload-section" 
                                    @dragenter="dragEnter($event)" 
                                    @dragleave="dragLeave($event)" 
                                    @dragover="dragOver($event)" 
                                    @drop="drop($event)">
                                    <div class="file-upload">
                                        <input  id="file-input" type="file" name="file" multiple class="btn blue-btn small-btn" @change="getFilesFromInput" />
                                    </div>
                                    <h3>{{ dragDropFilesText }}</h3>
                                    <div class="svg-container"><file-open-svg></file-open-svg></div>
                                    <button :disabled="draggedFileEnteredArea" class="btn blue-btn select-files-button" @click.prevent="clickFileUploadInput()" >
                                        <span v-show="filesForUpload.length === 0">Choose File(s)</span>
                                        <span v-show="filesForUpload.length > 0">Upload More</span>
                                    </button>
                                </div> 
                                </div>
                                <div class="upload-information-section">
                                    <p>Maximum upload file size: <strong>100 MB</strong></p>
                                </div>
                                <div v-show="filesForUpload.length > 0" class="upload-progress-section" >
                                    <div v-for="file in filesForUpload" class="file-progress">
                                        <div class="uploaded-file-details">
                                            <div class="svg-container">
                                                <div v-if="file.simplifiedFileType == ('blank')"><blank-file-svg></blank-file-svg></div>
                                                <div v-else-if="file.simplifiedFileType == ('document')"><paper-svg></paper-svg></div>
                                                <div v-else-if="file.simplifiedFileType == ('video')"><play-button-svg></play-button-svg></div>
                                                <div v-else-if="file.simplifiedFileType == ('pdf')"><pdf-file-svg></pdf-file-svg></div>
                                                <div v-else-if="file.simplifiedFileType == ('archive')"><zip-file-svg></zip-file-svg></div>
                                                <div v-else-if="file.simplifiedFileType == ('excel')"><excel-file-svg></excel-file-svg></div>
                                                <div v-else-if="file.simplifiedFileType == ('image')"><image-file-svg></image-file-svg></div>
                                                <div v-else-if="file.simplifiedFileType == ('presentation')"><presentation-file-svg></presentation-file-svg></div>
                                                <div v-else><blank-file-svg></blank-file-svg></div>
                                            </div>
                                            <p>{{ file.name }}</p>
                                        </div>
                                        <div class="uploaded-file-metrics">
                                            <div v-if="file.isFileTooLarge" class="file-too-large">
                                                <p>File is too large for upload!</p>
                                            </div>
                                            <div v-else class="progress-area">
                                                <p class="file-progress-text">{{  file.percentDone === '100%' ? 'Completed!' : file.percentDone  }}</p>
                                                <div class="progress">
                                                    <div class="progress-bar" :class="{ 'complete': file.complete }" role="progressbar" :style="{ width: file.percentDone }" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="divider"><strong class="text">OR</strong></div>
                            <div class="upload-link-container">
                                <app-input v-model="link" :label="'Add a Link'"/>
                                <app-button :onClick="uploadLink" :disabled="isUploadingFiles" :loading="isUploadingLink" :class="{ 'loading-link': isUploadingLink }">
                                    Add
                                </app-button>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-show="step == 'EditName'" class="edit-name">
                    <div class="preview-image-wrapper">
                        <div v-if="enrichedLink.previewUrl" :style="{ 'background-image': 'url(\'' + enrichedLink.previewUrl + '\')' }" class="preview-image-container"></div>
                        <div v-else class="svg-container"><weblink-files-svg></weblink-files-svg></div>
                    </div>
                    <app-input v-model="enrichedLink.name" :label="'File name'"/>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" :onClick="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <button v-if="step == 'EditName'" class="transparent-btn btn normal" @click="goToAddCollateral">Back</button>
                    <app-button :onClick="steps[step].action" :loading="steps[step].loading" :class="{ 'loading-link': steps[step].loading }">
                        {{ steps[step].buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

import BlankFileSvg from '@/components/svg/blank-file-svg';
import ExcelFileSvg from '@/components/svg/excel-file-svg';
import PdfFileSvg from '@/components/svg/pdf-file-svg';
import ZipFileSvg from '@/components/svg/zip-file-svg';
import ImageFileSvg from '@/components/svg/image-file-svg';
import PaperSvg from '@/components/svg/doc-file-svg';
import PlayButtonSvg from '@/components/svg/play-button-svg';
import WebLinkSvg from '@/components/svg/weblink-file-svg';
import FileOpen from '@/components/svg/file-open-svg';
import PresentationFile from '@/components/svg/presentation-file-svg';
import AppInput from '@/components/common/app-input';

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'add-collateral-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'blank-file-svg': BlankFileSvg,
        'excel-file-svg': ExcelFileSvg,
        'image-file-svg': ImageFileSvg,
        'pdf-file-svg': PdfFileSvg,
        'zip-file-svg': ZipFileSvg,
        'presentation-file-svg': PresentationFile,
        'paper-svg': PaperSvg,
        'play-button-svg': PlayButtonSvg,
        'file-open-svg': FileOpen,
        'weblink-files-svg': WebLinkSvg,
        'app-input': AppInput   
    },

    props: {
        widget: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            clientId: null,
            agencyId: null,
            isInitializing: true,
            selectedItems: [],
            step: null,
            draggedFileEnteredArea: false,
            isPreviouslyUploaded: false,
            isUploadingFiles: false,
            link: null,
            isUploadingLink: false,
            enrichedLink: {
                previewUrl: null,
                name: null,
                url: null,
                mimeType: null,
                fileExtension: null,
                fileSizeInBytes: null,
                uploadedByUserId: null,
                clientId: null,
                agencyId: null
            },

            dragDropFilesText: 'Drag Your Files Here Or',
            filesForUpload: [],
            

            steps: {
                AddCollateral: {
                    title: 'Add New Collateral',
                    buttonText: this.widget.isFromStudio ? 'Back to Studio': 'Back to marketing collaterals',
                    action: this.closeWidget,
                    loading: false
                },
                
                EditName: {
                    title: 'Edit Name',
                    buttonText: 'Save',
                    action: this.postLink,
                    loading: false
                }
            }
        }
    },

    computed: {
        userId() {
            return this.$store.state.user.id;
        },

        areSelectedItemsEmpty() {
            return this.selectedItems.length == 0;
        },
    },
    
    created() {
        this.clientId = this.widget.clientId;
        this.agencyId = this.widget.agencyId;
        this.step = 'AddCollateral';
    },

    methods: {
        emitSelectedItems() {
            var payload = {
                selectedItems: this.selectedItems
            };

            EventBus.$emit('library-payload-emitted', payload);
            this.closeWidget();
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        setParentFocus(event) {
            event.target.parentElement.classList.add('active');
        },
        
        removeParentFocus(event) {
            event.target.parentElement.classList.remove('active');
        },

        updateSelectedItems(event) {
            this.selectedItems = event;
        },

        postLink() {
            var request = {
                Url: this.enrichedLink.url,
                Name: this.enrichedLink.name,
                MimeType: this.enrichedLink.mimeType,
                FileExtension: this.enrichedLink.fileExtension,
                FileType: this.enrichedLink.fileType,
                FileSizeInBytes: this.enrichedLink.fileSizeInBytes,
                PreviewImageUrl: this.enrichedLink.previewUrl,
                UploadedByUserId: this.userId,
                ClientId: this.clientId,
                AgencyId: this.agencyId
            };

            this.steps.EditName.loading = true;

             this.$api.post('/api/Collaterals', request)
                .then(response => {
                    this.sendSuccessNotification(response);

                    this.link = null;
                    this.steps.EditName.loading = false;
                    this.step = 'AddCollateral';
                    EventBus.$emit('collateral:update');
                    EventBus.$emit('my-creations:update');  
                })
                .catch(response => {
                    this.steps.EditName.loading = false;
                    this.sendGeneralFailNotification(response);
                })
                .finally(() => {
                    this.closeWidget();
                });
        },

        uploadLink() {
            this.isUploadingLink = true;

            var request = {
                Links: [{
                    Url: this.link,
                }]
            };

             this.$api.post('/api/Collaterals/Links/Enrich', request)
                .then(response => {
                    this.hydrateEnrichedLinks(response.data.Links[0]);

                    this.isUploadingLink = false;
                    this.step = 'EditName';
                })
                .catch(response => {
                    this.isUploadingLink = false;
                    this.sendGeneralFailNotification(response);
            });
        },

        hydrateEnrichedLinks(data) {
            this.enrichedLink.previewUrl = data.ImageUrl;
            this.enrichedLink.url = data.Url;
            this.enrichedLink.name = data.Name ? data.Name : data.Title;
            this.enrichedLink.title = data.Title;
            this.enrichedLink.mimeType = data.MimeType;
            this.enrichedLink.fileExtension = data.FileExtension;
            this.enrichedLink.fileType = data.FileType;
            this.enrichedLink.fileSizeInBytes = data.FileSizeInBytes;
            this.enrichedLink.description = data.Description;
            this.enrichedLink.domain = data.domain;
            this.enrichedLink.uploadedByUserId = this.userId;
            this.enrichedLink.clientId = this.clientId;
            this.enrichedLink.agencyId = this.agencyId;
        },

        sendGeneralFailNotification(response) {
            var message = '';

            if(response.response.data.Message) {
                message = response.response.data.Message;
            }
            else {
                message = response.response.statusText;
            }
            
            this.$helpers.showFailNotification(message, { error: response });
        },

        sendSuccessNotification() {
            var message = `Added successfully: <strong>${ this.enrichedLink.name }</strong>`;
          
            this.$helpers.showSuccessNotification(message);
        },

        goToAddCollateral() {
            this.step = "AddCollateral";
        },

        dragEnter: function (e) {
            e.preventDefault();
            this.draggedFileEnteredArea = true;
            this.dragDropFilesText = 'Drop Your Files';
        },

        dragLeave: function (e) {
            e.preventDefault();
            this.draggedFileEnteredArea = false;
            this.dragDropFilesText = 'Drag Your Files Here Or';
        },

        dragOver: function (e) {
            e.preventDefault();
        },

        drop: function (e) {
            e.preventDefault();

            this.draggedFileEnteredArea = false;

            var dt = e.dataTransfer;
            var files = dt.files;

            this.iterateOverAndValidateFiles(files);
        },

        getFilesFromInput: function() {
            var files = $('#file-input')[0].files; // JH update jQuery selector
            this.iterateOverAndValidateFiles(files);
        },

        iterateOverAndValidateFiles: function (files) {
            for (var i = 0; i < files.length; i++) {

                var file = this.createFile(files[i]);
                this.filesForUpload.push(file);

                if (files[i].size <= 100000000) {
                    this.addFileToFormData(files[i], file);

                } else {
                    file.isFileTooLarge = true;
                }
            }

            this.isPreviouslyUploaded = true;
        },

        createFile(file) {
            file = {
                name: this.$helpers.sanitizeFileName(file.name),
                extension: file.name.split('.').splice(-1, 1).join(),
                simplifiedFileType: this.$helpers.getDocumentTypeFromMimeType(file.type),
                percentDone: '0%',
                complete: false,
                isFileTooLarge: false
            };

            return file;
        },

        
        clickFileUploadInput: function() {
            var $fileInput = $('#file-input');

            if (this.isPreviouslyUploaded) {
                $fileInput.val(null);
            }

            $fileInput.click();
        },

        addFileToFormData: function (fileForSend, fileForDisplay) {
            this.isFileChosen = true;
            var formData = new FormData();

            formData.append('file', fileForSend, this.$helpers.sanitizeFileName(fileForSend.name));
            formData.append('clientId', this.clientId);
            formData.append('agencyId', this.agencyId);

            this.sendUploadFileRequest(formData, fileForDisplay);
        },

        sendUploadFileRequest: function (formData, fileForDisplay) {
            var self = this;

            self.isUploadingFiles = true;

            self.$api.ajax({
                url: '/api/Collaterals/File',
                method: 'POST',
                data: formData,
                contentType: false,
                processData: false,
                onUploadProgress: function(progressEvent) {
                    self.onUploadProgress(progressEvent, fileForDisplay);
                }
            })
            .then(() => {
                fileForDisplay.percentDone = '100%';
                fileForDisplay.complete = true;

                $('#file-input').val(null);

                this.isUploadingFiles = false;
                EventBus.$emit('collateral:update');
                EventBus.$emit('my-creations:update');
            })
            .catch((error) => {
                this.$helpers.showFailNotification('An error occurred while saving your collateral piece', { error });
            })
            .finally(() => {
                self.isUploadingFiles = false;
            });
        },

        onUploadProgress: function (event, file) {
            var percent = event.loaded / event.total * 100;
            percent = Math.floor(percent);

            if (percent <= 100) {
                file.percentDone = percent + '%';
            }
        },
    },

}
</script>

<style scoped>
.add-collateral-widget {
    width: 100%;
}

.widget.add-collateral-widget .content-wrapper {
    padding: 0;
}

.add-collateral-widget  .widget-header-actions {
    display: flex;
}

.add-collateral-widget .action-container {
    display: flex;
}

.add-collateral-widget .action-container .btn {
    margin-left: 15px;
}

.add-collateral-widget .btn.transparent-btn {
    background: transparent;
    color: #877D88;
}

.add-collateral-widget .action-container .btn.transparent-btn {
    background: transparent;
    color: #18AEFF;
}

.add-collateral-widget .action-container .btn.transparent-btn:hover {
    background: #DBEDFA;
}

.add-collateral-widget .btn.transparent-btn:hover {
    background: #E1E0E5;
}

.add-collateral-widget .action-container .btn.white-btn {
    background: #FFFFFF;
    color: #877D88;
    border: 2px solid #E9E9EF;
}

.add-collateral-widget .action-container .btn.normal {
    height: 40px;
    border-radius: 20px;
    padding: 0px 25px;
    font-size: 14px;
    line-height: 17px;
}

.add-collateral-widget .action-container .btn.normal:hover {
    transition: background-color 250ms ease-in;
}

.add-collateral-widget .accent-color {
    color: #877D88;
}

.add-collateral-widget .loading-link {
    background-color: var(--color-fill-accent-skyblue-medium);  /*mass replace */
    color: #fff;
}

.add-collateral-widget .add-item-container {
    width: 100%;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-pack: justify;
    -ms-flex-pack: justify;
    justify-content: space-between;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -ms-flex-direction: column;
    flex-direction: column;
}

.add-collateral-widget .add-item-container .add-item {
    padding: 25px;
    width: 100%;
    height: 100%;
}

.add-collateral-widget .add-item-inner-container {
    position: relative;
    z-index: 0;
    height: 100%;
    width: 100%;    
}

.add-collateral-widget .add-item-upload-section {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    -webkit-box-pack: center;
        -ms-flex-pack: center;
            justify-content: center;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
        -ms-flex-direction: column;
            flex-direction: column;
    text-align: center;
    border-radius: 10px;
    border: 2px dashed #382153;
    width: 100%;
    height: 230px;
    margin-bottom: 10px;

    -webkit-box-shadow: inset 0px 2px 5px 1px rgba(0,0,0,0.3);
    -moz-box-shadow: inset 0px 2px 5px 1px rgba(0,0,0,0.3);
    box-shadow: inset 0px 2px 5px 1px rgba(0,0,0,0.3);
}

.add-collateral-widget .add-item-upload-section.dropping-files {
    /* background-color: #f3f4f9; */
    background-color: rgba(24, 174, 255, 0.31);
    -webkit-box-shadow: none;
    box-shadow: none;
}

.add-collateral-widget .upload-information-section,
.add-collateral-widget .upload-progress-section {
    margin-bottom: 15px;
}

.add-collateral-widget .file-progress {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-pack: justify;
    -ms-flex-pack: justify;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    position: relative;
}

.add-collateral-widget .file-progress svg {
    display: block;
    width: 20px;
    height: 25px;
    margin: 0 auto;
}

.add-collateral-widget .file-progress p {
    margin: 0;
}

.add-collateral-widget .file-progress .progress {
    width: 100%
}

.add-collateral-widget .file-progress .file-progress-text {
    width: 100%;
    display: block;
    text-align: right;
    margin: 0;
}

.add-collateral-widget .file-progress .progress .progress-bar {
    background-color: #27dc44;
}

.add-collateral-widget .progress .progress-bar.complete {
    background-color: #17b8ff;
}

.add-collateral-widget .add-item-upload-section h3 {
    position: relative;
    z-index: -1;
    margin-bottom: 10px;
}

.add-collateral-widget .add-item-upload-section .select-files-button {
    font-size: 14px;
}

.add-collateral-widget .add-item-inner-container .file-upload {
    display: none;
}

.add-collateral-widget .add-item-container .btn-container {
    text-align: right;
    width: 100%;
    background-color: #f7f7f8;
    padding: 10px;
    border: 1px solid #cac6cb;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
}

.add-collateral-widget .uploaded-file-details {
    display: flex;
    width: 200px;
}

.add-collateral-widget .svg-container {
    margin-right: 10px;
}

.add-collateral-widget .uploaded-file-details p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.add-collateral-widget .uploaded-file-metrics {
    width: 335px;
    margin-left: 10px;
}

.add-collateral-widget .add-item-upload-section .svg-container {
    position: relative;
    z-index: -1;
    margin-bottom: 10px;
    margin-right: 0;
}

.add-collateral-widget .add-item-upload-section .svg-container svg {
    width: 103px;
}

.add-collateral-widget .divider {
    position: relative;
    margin: 20px 0 30px;
    border-bottom: solid 2px #E9E9EF;
}

.add-collateral-widget .divider .text {
    position: absolute;
    padding: 5px 80px;
    background-color: #fff;
    left: calc(50% - 91px);
    top: calc(50% - 15px);
}

.add-collateral-widget .bg-wrapper {
    position: relative;
    z-index: 0;
    background-color: #e9e9ef;
    border-radius: 10px;
}

.add-collateral-widget .upload-link-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.add-collateral-widget .upload-link-container .app-input {
    width: 455px;
}

.add-collateral-widget .preview-image-container {
    width: 100%;
    height: 268px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
}

.add-collateral-widget .preview-image-wrapper {
    width: 100%;
    height: 268px;
    margin-bottom: 15px;
}

.add-collateral-widget .edit-name {
    padding: 25px;
}

.add-collateral-widget .preview-image-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
}

.add-collateral-widget .preview-image-wrapper .svg-container {
    margin: 0;
    text-align: center;
    width: 100%;
}

.add-collateral-widget .preview-image-wrapper .svg-container svg {
    width: 130px;
}
</style>